import { BaseDragger } from "./BaseDragger";
import type { OneWayDragger } from "./OneWayDragger";
import type { TwoPointDragger } from "./TwoPointDragger";
import type {
  DraggerConfig,
  Point2D,
  Point3D,
  SnapOptions,
  TypedInputSpec,
} from "./types";
import type { DRDrawingViewer } from "../DRDrawingViewer";

/**
 * Thin router combining a classic single-stroke engine (OneWayDragger) and a
 * two-point sequential-placement engine (TwoPointDragger) behind a single
 * registered dragger, toggled at runtime via setTwoPointMode().
 *
 * A class can only extend one base, so a Rectangle dragger can't be both a
 * OneWayDragger and a TwoPointDragger at once — composition, not branching
 * inside either engine, is what lets one DraggerType support both
 * interaction models. Both engines share the same DraggerConfig object
 * (passed once, reused for both), so autoSelect/snapOptions stay consistent
 * regardless of which engine is currently active.
 */
export abstract class DualModeDragger<
  TClassic extends OneWayDragger,
  TSequential extends TwoPointDragger,
> extends BaseDragger {
  private readonly oneWayEngine: TClassic;
  private readonly twoPointEngine: TSequential;
  private twoPointMode = false;

  private pendingChangeHandlers: Array<(pending: boolean) => void> = [];

  constructor(
    viewer: DRDrawingViewer,
    config: DraggerConfig,
    oneWayEngine: TClassic,
    twoPointEngine: TSequential,
  ) {
    super(viewer, config);
    this.oneWayEngine = oneWayEngine;
    this.twoPointEngine = twoPointEngine;

    // Forward the two-point engine's pending-placement signal through this
    // router's own stable pub/sub. Subscribed once here (not per-activation),
    // so external subscribers (e.g. a cancel button) don't need to
    // re-subscribe whenever setTwoPointMode() swaps which engine is active —
    // Facade.tools.current stays the same router instance for as long as
    // this DraggerType is the active tool.
    this.twoPointEngine.onPendingPlacementChange((pending) => {
      for (const handler of this.pendingChangeHandlers) {
        handler(pending);
      }
    });

    // Same reasoning for the typed input consumed by the keypad panel: it
    // subscribes to this router (Facade.tools.current), so the engine's updates
    // have to be re-broadcast through a handler list that survives
    // setTwoPointMode() swapping engines. Re-emitting from the router picks the
    // spec back up via getTypedInputSpec() below, which delegates to the engine.
    this.twoPointEngine.onTypedInputChange(() => {
      this.emitTypedInputChange();
    });
  }

  private get activeEngine(): BaseDragger {
    return this.twoPointMode ? this.twoPointEngine : this.oneWayEngine;
  }

  override setTwoPointMode(enabled: boolean): void {
    if (this.twoPointMode === enabled) return;
    // Deactivating the currently-active engine cancels/discards anything
    // half-placed (see TwoPointDragger.onDeactivate) before switching model.
    if (this.isActive) this.activeEngine.deactivate();
    this.twoPointMode = enabled;
    if (this.isActive) this.activeEngine.activate();
  }

  protected onActivate(): void {
    this.activeEngine.activate();
  }

  protected onDeactivate(): void {
    this.activeEngine.deactivate();
  }

  override handleStart(
    screen: Point2D,
    world: Point3D,
    ev: PointerEvent,
  ): void {
    this.activeEngine.handleStart(screen, world, ev);
  }

  override handleMove(screen: Point2D, world: Point3D, ev: PointerEvent): void {
    this.activeEngine.handleMove(screen, world, ev);
  }

  override handleEnd(screen: Point2D, world: Point3D, ev: PointerEvent): void {
    this.activeEngine.handleEnd(screen, world, ev);
  }

  override handleCancel(): void {
    this.activeEngine.handleCancel();
  }

  // Delegates to whichever engine is active: the one-way engine's inherited
  // no-op when twoPointMode is off (no hover-follow outside two-point mode,
  // by design — see TwoPointModeBtn), the two-point engine's real
  // implementation when it's on.
  override handleHoverMove(screen: Point2D, world: Point3D): void {
    this.activeEngine.handleHoverMove(screen, world);
  }

  override handleLeave(
    screen: Point2D,
    world: Point3D,
    ev: PointerEvent,
  ): void {
    this.activeEngine.handleLeave(screen, world, ev);
  }

  override getSnapReferencePoint(): Point3D | null {
    return this.activeEngine.getSnapReferencePoint();
  }

  override snapsToGestureStart(): boolean {
    return this.activeEngine.snapsToGestureStart();
  }

  override getSnapOptions(): SnapOptions | undefined {
    return this.activeEngine.getSnapOptions();
  }

  // Delegate straight to twoPointEngine (not activeEngine): its own pending
  // state is always accurate regardless of which engine is currently active,
  // since deactivating it (including via setTwoPointMode(false)) always
  // clears it — see TwoPointDragger.onDeactivate()/clear().
  override hasPendingPlacement(): boolean {
    return this.twoPointEngine.hasPendingPlacement();
  }

  override onPendingPlacementChange(
    handler: (pending: boolean) => void,
  ): () => void {
    this.pendingChangeHandlers.push(handler);
    return () => {
      const index = this.pendingChangeHandlers.indexOf(handler);
      if (index >= 0) {
        this.pendingChangeHandlers.splice(index, 1);
      }
    };
  }

  override cancelPendingPlacement(): void {
    this.twoPointEngine.cancelPendingPlacement();
  }

  // Typed-input contract — same delegation rule as above: always the two-point
  // engine, which returns a null spec on its own whenever point A isn't placed
  // (including while the one-way engine is the active one), so the pad stays
  // off screen in single-stroke mode without the router having to know.
  override getTypedInputSpec(): TypedInputSpec | null {
    return this.twoPointEngine.getTypedInputSpec();
  }

  override previewTypedInput(
    modeId: string,
    values: Record<string, number>
  ): void {
    this.twoPointEngine.previewTypedInput(modeId, values);
  }

  override commitTypedInput(
    modeId: string,
    values: Record<string, number>
  ): void {
    this.twoPointEngine.commitTypedInput(modeId, values);
  }

  // handleStart/handleMove/handleEnd are overridden above to delegate
  // directly to the active engine, so BaseDragger's onStart/onDrag/onEnd
  // hooks are never invoked on the router itself — these only exist to
  // satisfy BaseDragger's abstract contract.
  protected onStart(): void {}
  protected onDrag(): void {}
  protected onEnd(): void {}
}
